import React, { useCallback, useEffect, useRef, useState } from "react";
import type { Editor } from "@tiptap/react";
import { Copy, Eye, FileStack, Loader2, Plus, RotateCcw, Save, Star, Trash2 } from "lucide-react";
import { api, unwrapArray, unwrapObject } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { useConfirm } from "@/context/ConfirmContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { SectionsEditor, sectionProblem, toApiSections, toEditableSections, type EditableSection } from "@/components/proposals/SectionsEditor";
import { MergeFieldPanel } from "@/components/proposals/MergeFieldPanel";
import { AgreementDocument } from "@/components/agreements/AgreementDocument";
import { AGREEMENT_TYPE_LABELS, type AgreementRender, type AgreementTemplate, type AgreementType } from "@/types/agreement";

interface Draft {
  id: string | null;
  name: string;
  description: string;
  agreement_type: AgreementType;
  default_title: string;
  term_months: string;
  auto_renew: boolean;
  renewal_notice_days: string;
  is_default: boolean;
  status: boolean;
  sections: EditableSection[];
}

const blank = (): Draft => ({
  id: null,
  name: "",
  description: "",
  agreement_type: "SUPPLY",
  default_title: "",
  term_months: "12",
  auto_renew: false,
  renewal_notice_days: "30",
  is_default: false,
  status: true,
  sections: toEditableSections([
    { type: "cover", title: "Agreement", html: null, variant: "compact" },
    { type: "rich_text", title: "Parties", html: "<p>This agreement is made on {{agreement.effective_date}} between <strong>{{company.name}}</strong> and <strong>{{customer.company}}</strong>.</p>" },
    { type: "estimate", title: "Price schedule", html: "<p>Prices are as agreed in quotation {{quote.number}}.</p>" },
    { type: "acceptance", title: "IN WITNESS WHEREOF", html: "<p>The parties have signed this agreement.</p>", numbered: false },
  ]),
});

const fromTemplate = (t: AgreementTemplate): Draft => ({
  id: t.id,
  name: t.name,
  description: t.description || "",
  agreement_type: t.agreement_type,
  default_title: t.defaults?.title || "",
  term_months: String(t.defaults?.term_months ?? 12),
  auto_renew: !!t.defaults?.auto_renew,
  renewal_notice_days: String(t.defaults?.renewal_notice_days ?? 30),
  is_default: t.is_default,
  status: t.status !== false,
  sections: toEditableSections(t.sections || []),
});

/** Settings → Agreement templates: the clauses, term and renewal reps start from when they make an agreement */
export function AgreementTemplatesPage() {
  const confirm = useConfirm();
  const { success, error } = useToast();
  const [templates, setTemplates] = useState<AgreementTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [mergeFields, setMergeFields] = useState<Array<{ key: string; label: string }>>([]);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const activeEditor = useRef<Editor | null>(null);
  const [preview, setPreview] = useState<AgreementRender | null>(null);
  const [previewError, setPreviewError] = useState(false);

  const loadList = useCallback(async () => {
    try {
      const list = unwrapArray<AgreementTemplate>(unwrapObject(await api.get("/api/AgreementTemplate/List?all=true")));
      setTemplates(list);
      return list;
    } catch (err: any) {
      error("Couldn't load templates", err.message || "Something went wrong");
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const open = useCallback(async (id: string) => {
    try {
      const t = unwrapObject<AgreementTemplate>(await api.get(`/api/AgreementTemplate/ById/${id}`));
      if (t.merge_fields) setMergeFields(t.merge_fields);
      setDraft(fromTemplate(t));
      setDirty(false);
      activeEditor.current = null;
    } catch (err: any) {
      error("Couldn't open the template", err.message || "Something went wrong");
    }
  }, []);

  useEffect(() => {
    loadList().then((list) => {
      const first = list.find((t) => t.is_default) || list[0];
      if (first) open(first.id);
    });
  }, []);

  useEffect(() => {
    if (!dirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  // Live preview: the unsaved clauses drawn with a sample customer and figures, a moment after the last keystroke
  useEffect(() => {
    if (!draft) return;
    let stale = false;
    const timer = setTimeout(async () => {
      try {
        const doc = unwrapObject<AgreementRender>(
          await api.post("/api/AgreementTemplate/Preview", {
            name: draft.name.trim() || "Agreement",
            agreement_type: draft.agreement_type,
            default_title: draft.default_title.trim() || undefined,
            term_months: Math.max(1, Math.round(Number(draft.term_months)) || 12),
            sections: toApiSections(draft.sections),
          }),
        );
        if (!stale) {
          setPreview(doc);
          setPreviewError(false);
        }
      } catch {
        if (!stale) setPreviewError(true);
      }
    }, 500);
    return () => {
      stale = true;
      clearTimeout(timer);
    };
  }, [draft?.sections, draft?.name, draft?.agreement_type, draft?.default_title, draft?.term_months]);

  const usedBy = templates.find((t) => t.id === draft?.id)?.used_by || 0;
  const wasDefault = !!templates.find((t) => t.id === draft?.id)?.is_default;
  const leaveOk = async () => !dirty || (await confirm({ title: "Discard your changes?", message: "The unsaved changes to this template will be lost.", confirmLabel: "Discard", destructive: true }));
  const change = (patch: Partial<Draft>) => {
    setDraft((d) => (d ? { ...d, ...patch } : d));
    setDirty(true);
  };

  const save = async (override?: Partial<Draft>) => {
    if (!draft) return;
    const d = { ...draft, ...override };
    const months = Math.round(Number(d.term_months));
    const notice = Math.round(Number(d.renewal_notice_days));
    const problem = !d.name.trim() ? "Give the template a name" : !(months >= 1 && months <= 120) ? "The term must be between 1 and 120 months" : !(notice >= 0 && notice <= 365) ? "The renewal reminder must be between 0 and 365 days" : sectionProblem(d.sections);
    if (problem) return error("Can't save yet", problem);
    setBusy(true);
    try {
      const body = {
        name: d.name.trim(),
        description: d.description.trim() || undefined,
        agreement_type: d.agreement_type,
        default_title: d.default_title.trim() || undefined,
        term_months: months,
        auto_renew: d.auto_renew,
        renewal_notice_days: notice,
        is_default: d.is_default,
        status: d.status,
        sections: toApiSections(d.sections),
      };
      const res = d.id ? await api.put(`/api/AgreementTemplate/Update/${d.id}`, body) : await api.post("/api/AgreementTemplate/Insert", body);
      const t = unwrapObject<AgreementTemplate>(res);
      success("Template saved", d.id ? `"${t.name}" updated` : `"${t.name}" created`);
      await loadList();
      await open(t.id);
    } catch (err: any) {
      error("Save failed", err.message || "Could not save the template");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!draft?.id) return;
    const msg = usedBy
      ? `"${draft.name}" was used for ${usedBy} agreement${usedBy === 1 ? "" : "s"}, so it will be deactivated (hidden from new agreements) rather than deleted. Continue?`
      : `Delete "${draft.name}"? This can't be undone.`;
    if (!(await confirm({ title: usedBy ? "Deactivate this template?" : "Delete this template?", message: msg, confirmLabel: usedBy ? "Deactivate" : "Delete", destructive: true }))) return;
    setBusy(true);
    try {
      const res = unwrapObject<{ removed: boolean; deactivated: boolean }>(await api.delete(`/api/AgreementTemplate/Delete/${draft.id}`));
      success(res.deactivated ? "Template deactivated" : "Template deleted", res.deactivated ? "It no longer appears for new agreements" : `"${draft.name}" removed`);
      setDirty(false);
      const list = await loadList();
      if (res.deactivated) await open(draft.id);
      else {
        const next = list.find((t) => t.is_default) || list[0];
        if (next) await open(next.id);
        else setDraft(null);
      }
    } catch (err: any) {
      error("Couldn't remove it", err.message || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  const startNew = async () => {
    if (!(await leaveOk())) return;
    setDraft(blank());
    setDirty(true);
    activeEditor.current = null;
  };

  const duplicate = async () => {
    if (!draft || !(await leaveOk())) return;
    setDraft({ ...draft, id: null, name: `Copy of ${draft.name}`.slice(0, 120), is_default: false, status: true, sections: toEditableSections(toApiSections(draft.sections).map(({ id, ...s }) => s)) });
    setDirty(true);
  };

  const input = "h-9 w-full rounded-md border border-input bg-background px-3 text-sm shadow-xs";

  return (
    <div className="space-y-6 pb-10">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Agreement Templates</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            The clauses, term and renewal reps start from. Merge fields like {"{{customer.company}}"} and {"{{agreement.end_date}}"} are filled in for each deal.
          </p>
        </div>
        <Button size="sm" onClick={startNew} className="h-9 gap-1.5 shadow-sm">
          <Plus className="size-4" /> New Template
        </Button>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-4 items-start">
        <div className="xl:col-span-3 space-y-4">
          <div className="bg-card border rounded-2xl overflow-hidden">
            <div className="px-4 py-3 border-b text-sm font-bold flex items-center gap-2">
              <FileStack className="size-4 text-primary" /> Templates
            </div>
            {loading ? (
              <div className="p-3 space-y-2">
                <Skeleton className="h-10" />
                <Skeleton className="h-10" />
              </div>
            ) : (
              <ul className="divide-y">
                {draft && !draft.id && <li className="px-4 py-2.5 bg-primary/5 text-sm font-semibold">{draft.name.trim() || "New template"} (unsaved)</li>}
                {templates.map((t) => (
                  <li key={t.id}>
                    <button
                      type="button"
                      onClick={async () => {
                        if (draft?.id !== t.id && (await leaveOk())) open(t.id);
                      }}
                      className={cn("w-full text-left px-4 py-2.5 hover:bg-muted/50", draft?.id === t.id && "bg-primary/5", t.status === false && "opacity-60")}
                    >
                      <div className="flex items-center gap-1.5">
                        <span className="text-sm font-semibold truncate">{t.name}</span>
                        {t.is_default && <Star className="size-3.5 text-warning fill-warning shrink-0" />}
                      </div>
                      <div className="text-[11px] text-muted-foreground">
                        {t.status === false ? "Inactive · " : ""}
                        {AGREEMENT_TYPE_LABELS[t.agreement_type]} · used for {t.used_by || 0}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          {draft && <MergeFieldPanel fields={mergeFields} activeEditor={activeEditor} showValues={false} />}
        </div>

        {draft ? (
          <>
          <div className="xl:col-span-5 space-y-4">
            <div className="bg-card border rounded-2xl p-4 space-y-3">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2">
                  {draft.is_default && <Badge variant="warning">Default</Badge>}
                  {!draft.status && <Badge variant="outline">Inactive</Badge>}
                  {dirty && <span className="text-xs text-warning">Unsaved changes</span>}
                </div>
                <div className="flex items-center gap-2">
                  {draft.id && (
                    <Button variant="outline" size="sm" className="gap-1.5" onClick={duplicate} disabled={busy}>
                      <Copy className="size-4" /> Duplicate
                    </Button>
                  )}
                  {draft.id && !draft.status && (
                    <Button variant="outline" size="sm" className="gap-1.5" onClick={() => save({ status: true })} disabled={busy}>
                      <RotateCcw className="size-4" /> Reactivate
                    </Button>
                  )}
                  {draft.id && !wasDefault && draft.status && (
                    <Button variant="outline" size="sm" className="gap-1.5 text-destructive" onClick={remove} disabled={busy}>
                      <Trash2 className="size-4" /> {usedBy ? "Deactivate" : "Delete"}
                    </Button>
                  )}
                  <Button size="sm" className="gap-1.5" onClick={() => save()} disabled={busy || !dirty}>
                    {busy ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />} Save
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="space-y-1 md:col-span-2">
                  <Label className="text-xs">Name *</Label>
                  <Input value={draft.name} maxLength={120} onChange={(e) => change({ name: e.target.value })} placeholder="e.g. Annual rate contract" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Kind</Label>
                  <select value={draft.agreement_type} onChange={(e) => change({ agreement_type: e.target.value as AgreementType })} className={input}>
                    {(Object.keys(AGREEMENT_TYPE_LABELS) as AgreementType[]).map((k) => (
                      <option key={k} value={k}>{AGREEMENT_TYPE_LABELS[k]}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-1 md:col-span-3">
                  <Label className="text-xs">When to use it</Label>
                  <Input value={draft.description} maxLength={500} onChange={(e) => change({ description: e.target.value })} placeholder="Shown to reps when they pick a template" />
                </div>
                <div className="space-y-1 md:col-span-3">
                  <Label className="text-xs">Title of a new agreement</Label>
                  <Input value={draft.default_title} maxLength={200} onChange={(e) => change({ default_title: e.target.value })} placeholder="Leave empty to use the deal's name and the kind" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Term (months)</Label>
                  <Input type="number" min={1} max={120} value={draft.term_months} onChange={(e) => change({ term_months: e.target.value })} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Remind the owner before it ends (days)</Label>
                  <Input type="number" min={0} max={365} value={draft.renewal_notice_days} onChange={(e) => change({ renewal_notice_days: e.target.value })} />
                </div>
                <label className="flex items-center gap-2.5 text-sm md:pt-5">
                  <Switch checked={draft.auto_renew} onCheckedChange={(v) => change({ auto_renew: v })} />
                  <span>Renews automatically</span>
                </label>
              </div>

              <label className="flex items-center gap-2.5 text-sm">
                <Switch checked={draft.is_default} disabled={wasDefault} onCheckedChange={(v) => change({ is_default: v, ...(v ? { status: true } : {}) })} />
                <span>
                  Default template
                  <span className="block text-xs text-muted-foreground">Pre-selected for new agreements. To change the default, make another template the default.</span>
                </span>
              </label>
              {usedBy > 0 && (
                <p className="text-xs text-muted-foreground">
                  Changes apply to new agreements only; {usedBy === 1 ? "the agreement already made from it keeps its own text." : `the ${usedBy} agreements already made from it keep their own text.`}
                </p>
              )}
              <p className="text-xs text-muted-foreground">Have a lawyer check the wording of any template before it is used with customers. Text in [square brackets] is for the rep to fill in for each customer.</p>
            </div>

            <SectionsEditor allowFacts sections={draft.sections} onChange={(sections) => change({ sections })} activeEditor={activeEditor} />
          </div>
          <div className="xl:col-span-4 xl:sticky xl:top-4 space-y-2">
            <div className="flex items-center gap-1.5 text-sm font-bold">
              <Eye className="size-4 text-primary" /> Live preview
            </div>
            <div className="rounded-2xl border bg-muted/30 p-3">
              <p className="text-[11px] text-muted-foreground mb-2">Updates as you type, with a sample customer and figures (₹10,500 + GST) and your company's details. Nothing here is saved.</p>
              {previewError && <p className="text-xs text-destructive mb-2">Couldn't draw the preview. It will retry on your next change.</p>}
              <div className="max-h-[calc(100vh-9rem)] overflow-y-auto">
                {preview ? (
                  <div className="[zoom:0.7]">
                    <AgreementDocument doc={preview} />
                  </div>
                ) : (
                  <Skeleton className="h-96" />
                )}
              </div>
            </div>
          </div>
          </>
        ) : (
          !loading && <div className="xl:col-span-9 rounded-2xl border border-dashed p-10 text-center text-sm text-muted-foreground">No templates yet. Create one to get started.</div>
        )}
      </div>
    </div>
  );
}
